import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Drag-to-resize width for a layout column.
 *
 * Returns the current width (px), whether a drag is in progress, and a
 * `startResize` handler to put on a thin grab-strip at the column's
 * edge. The drag is delta-based — we capture the cursor X and the width
 * at mouse-down and apply the difference on every move — so the strip
 * tracks the cursor exactly instead of snapping on the first move.
 *
 * `direction` says which way a rightward drag grows the column:
 *   - "right" (default): the grab strip is on the column's right edge,
 *     dragging right makes it wider (left-hand list).
 *   - "left": the grab strip is on the column's left edge, dragging
 *     right makes it narrower (right-hand rail).
 *
 * Width persists under `storageKey` in localStorage so the layout a
 * user settles on survives reloads. Stored values outside [min, max]
 * are clamped on read so a stale key can't produce a broken layout,
 * and the live width re-clamps whenever `min` / `max` change.
 *
 * While dragging, `document.body` gets `data-column-resizing="true"`.
 * Pages whose middle pane hosts an iframe (the Apryse viewer) should
 * turn off pointer events on it during a drag — otherwise the iframe
 * swallows mousemove and the drag stalls as soon as the cursor crosses
 * into the document.
 */
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const readStoredWidth = (storageKey, fallback, min, max) => {
  if (!storageKey) return fallback;
  try {
    const raw = globalThis.localStorage?.getItem(storageKey);
    const parsed = raw == null ? NaN : Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return clamp(parsed, min, max);
  } catch {
    return fallback;
  }
};

const writeStoredWidth = (storageKey, width) => {
  if (!storageKey) return;
  try {
    globalThis.localStorage?.setItem(storageKey, String(Math.round(width)));
  } catch {
    // Storage can be unavailable (private mode, quota); the width still
    // works for this session.
  }
};

const clientXOf = (event) => {
  if (event.touches && event.touches.length > 0) return event.touches[0].clientX;
  if (event.changedTouches && event.changedTouches.length > 0) {
    return event.changedTouches[0].clientX;
  }
  return event.clientX;
};

const useResizableWidth = ({
  storageKey = null,
  defaultWidth,
  min,
  max,
  direction = "right",
}) => {
  const [width, setWidth] = useState(() =>
    readStoredWidth(storageKey, defaultWidth, min, max)
  );
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef({ startX: 0, startWidth: 0 });
  const widthRef = useRef(width);
  widthRef.current = width;

  // Bounds can move after mount (the caller derives `max` from the
  // viewport and the other columns). Re-clamp whenever they do so a
  // remembered wide column can't crowd out its neighbours on a smaller
  // screen than the one it was set on.
  useEffect(() => {
    setWidth((current) => clamp(current, min, max));
  }, [min, max]);

  const startResize = useCallback(
    (event) => {
      // Primary button only for mouse; touch has no `button`.
      if (event.button != null && event.button !== 0) return;
      event.preventDefault?.();
      dragRef.current = { startX: clientXOf(event), startWidth: widthRef.current };
      setIsResizing(true);
    },
    []
  );

  useEffect(() => {
    if (!isResizing) return undefined;

    const sign = direction === "left" ? -1 : 1;
    const handleMove = (event) => {
      const delta = (clientXOf(event) - dragRef.current.startX) * sign;
      setWidth(clamp(dragRef.current.startWidth + delta, min, max));
    };
    const handleEnd = () => {
      setIsResizing(false);
      writeStoredWidth(storageKey, widthRef.current);
    };

    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.body.dataset.columnResizing = "true";

    globalThis.addEventListener("mousemove", handleMove);
    globalThis.addEventListener("mouseup", handleEnd);
    globalThis.addEventListener("touchmove", handleMove, { passive: true });
    globalThis.addEventListener("touchend", handleEnd);
    globalThis.addEventListener("touchcancel", handleEnd);

    return () => {
      globalThis.removeEventListener("mousemove", handleMove);
      globalThis.removeEventListener("mouseup", handleEnd);
      globalThis.removeEventListener("touchmove", handleMove);
      globalThis.removeEventListener("touchend", handleEnd);
      globalThis.removeEventListener("touchcancel", handleEnd);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
      delete document.body.dataset.columnResizing;
    };
  }, [direction, isResizing, max, min, storageKey]);

  return { width, isResizing, startResize };
};

export default useResizableWidth;
